import { DOCUMENT } from "@angular/common";
import {
  AfterViewInit,
  ContentChildren,
  Directive,
  ElementRef,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  QueryList,
  TemplateRef,
  ViewContainerRef,
  inject,
} from "@angular/core";

import { Subject, fromEvent, takeUntil, merge, tap } from "rxjs";

import {
  DialogBoxService,
  DialogRef,
} from "../../services/confect-dialog-box.service";
import { DialogBoxToggleDirective } from "./dialog-box-toggle/dialog-box-toggle.directive";

@Directive({
  selector: "[dialogBox]",
  standalone: false,
  exportAs: "dialogBox",
})
export class DialogBoxDirective implements OnDestroy, OnInit, AfterViewInit {
  private dialogService = inject(DialogBoxService);
  private viewRef = inject(ViewContainerRef);
  private elementRef = inject(ElementRef);

  private readonly document = inject(DOCUMENT);

  unsubscribe$ = new Subject<any>();
  destroy$$ = new Subject<any>();

  @Input() dialogTemplate: TemplateRef<any>;
  @Input() dialogTitle: string;
  @Input({ required: true }) dialogPosition:
    | "above"
    | "below"
    | "left"
    | "right";
  @Input() dialogAlign: "top" | "bottom" | "right" | "left" | "center" =
    "center";
  @Input() dialogDisable: boolean = false;
  @Input() dialogFooter: TemplateRef<any>;

  dialogRef: DialogRef | null;

  @ContentChildren(DialogBoxToggleDirective, { read: ElementRef })
  children: QueryList<ElementRef>;

  open(e: MouseEvent) {
    if (this.dialogRef != null) {
      this.destroy();
      return;
    }
    if (this.dialogDisable) {
      return;
    }

    const boundingRect = this.elementRef.nativeElement.getBoundingClientRect();
    this.dialogRef = this.dialogService.open({
      ...(this.dialogTemplate != null && { template: this.dialogTemplate }),
      ...(this.dialogTitle != null && { boxTitle: this.dialogTitle }),
      footer: this.dialogFooter,
      position: this.dialogPosition,
      align: this.dialogAlign,
      parentPosition: boundingRect,
      event: e,
    });

    this.dialogRef?.afterClose.pipe(takeUntil(this.unsubscribe$)).subscribe({
      next: () => {
        this.dialogRef = null;
      },
    });
  }

  @HostListener("wheel", ["$event"]) scroll(e: MouseEvent) {
    this.destroy();
  }

  /** Inserted by Angular inject() migration for backwards compatibility */
  constructor(...args: unknown[]);

  constructor() {}

  destroy() {
    this.dialogRef?.close();
    this.unsubscribe$.next(true);
  }

  ngOnInit(): void {}

  ngAfterViewInit(): void {
    const nativeElement = this.viewRef.element.nativeElement;

    const dialogToggles =
      this.children.length > 0
        ? this.children.map((elRef: ElementRef) => elRef.nativeElement)
        : [];

    const click$ = (
      dialogToggles.length > 0
        ? merge(...dialogToggles.map((dt) => fromEvent(dt, "click")))
        : fromEvent(nativeElement, "click")
    ).pipe(
      takeUntil(this.destroy$$), // clear subscription
      tap((event: MouseEvent) => {
        this.open(event);
        event.stopPropagation();
      }),
    );

    click$.subscribe();

    const docClick$ = fromEvent(document, "click").pipe(
      takeUntil(this.destroy$$), // clear subscription
      tap((event: MouseEvent) => {
        if (this.dialogRef != null) {
          this.dialogRef.close();
        }
      }),
    );

    docClick$.subscribe();
  }

  ngOnDestroy(): void {
    if (this.dialogRef != null) {
      this.dialogRef.close();
    }
    this.unsubscribe$.next(true);
    this.destroy$$.next(true);
  }
}
